import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Trash2, Pencil, Plus, Save } from 'lucide-react';
import { useSchoolYear } from '../contexts/SchoolYearContext';
import toast from 'react-hot-toast';

// Simple color palette for categories
const CATEGORY_COLORS = {
  cours: 'bg-green-100 text-green-800 border border-green-200',
  tp: 'bg-orange-100 text-orange-800 border border-orange-200',
  td: 'bg-blue-100 text-blue-800 border border-blue-200',
  cours_tp: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
  cours_td: 'bg-lime-100 text-lime-800 border border-lime-200',
  projet: 'bg-violet-100 text-violet-800 border border-violet-200',
  sae: 'bg-violet-100 text-violet-800 border border-violet-200',
  evaluation: 'bg-red-100 text-red-800 border border-red-200',
  ccf: 'bg-pink-100 text-pink-800 border border-pink-200',
  vacance: 'bg-yellow-100 text-yellow-900 border border-yellow-200',
  feries: 'bg-gray-100 text-gray-700',
  stage: 'bg-blue-900 text-white',
  examens: 'bg-red-900 text-white',
  evenement: 'bg-indigo-200 text-indigo-900',
};

// Fonction pour formater la durée au format "1h10" ou "30min"
const formatDuree = (dureeH) => {
  if (!dureeH || dureeH === 0) return '0h';
  
  const heures = Math.floor(dureeH);
  const minutes = Math.round((dureeH - heures) * 60);
  
  if (heures === 0) {
    return `${minutes}min`;
  }
  
  if (minutes === 0) {
    return `${heures}h`;
  }
  
  return `${heures}h${minutes}`;
};

// Référentiel synthétique des compétences (code -> définition)
const COMPETENCE_LABELS = {
  C1: "recenser et prendre en compte les normes, les réglementations applicables au projet/chantier",
  C2: "extraire les informations nécessaires à la réalisation des tâches",
  C3: "gérer les risques et les aléas liés à la réalisation des tâches",
  C4: "communiquer de manière adaptée à l'oral, à l'écrit, y compris en langue anglaise",
  C5: "interpréter un besoin client/utilisateur, un CCTP, un cahier des charges",
  C6: "modéliser le comportement de tout ou partie d’un ouvrage, d’une installation, d’un équipement électrique",
  C7: "simuler le comportement de tout ou partie d’un ouvrage, d’une installation, d’un équipement électrique",
  C8: "dimensionner les constituants d’un ouvrage, d’une installation, d’un équipement électrique",
  C9: "choisir les constituants d’un ouvrage, d’une installation, d’un équipement électrique",
  C10: "proposer l’architecture d’un ouvrage, d’une installation, d’un équipement électrique",
  C11: "réaliser les documents du projet/chantier (plans, schémas, maquette virtuelle, etc.)",
  C12: "gérer et conduire (organisation, planification, suivi, pilotage, réception, etc.) le projet/chantier",
  C13: "mesurer les grandeurs caractéristiques d’un ouvrage, d’une installation, d’un équipement électrique",
  C14: "réaliser un ouvrage, une installation, un équipement électrique",
  C15: "configurer et programmer les matériels dans le cadre du projet/chantier",
  C16: "appliquer un protocole pour mettre en service un ouvrage, une installation, un équipement électrique",
  C17: "réaliser un diagnostic de performance (énergétique, sécurité, etc.) d’un ouvrage/installation/équipement",
  C18: "réaliser des opérations de maintenance sur un ouvrage, une installation, un équipement électrique",
};
const COMPETENCE_LIST = Object.entries(COMPETENCE_LABELS).map(([code, label]) => ({ code, label }));

// Référentiel des tâches (code -> libellé)
const TASK_LABELS = {
  'T 1.1': "analyser et/ou élaborer les documents relatifs aux besoins du client/utilisateur",
  'T 1.2': "élaborer un avant-projet/chantier (ou avant-projet sommaire)",
  'T 1.3': "dimensionner les constituants de l’installation",
  'T 1.4': "définir les coûts pour préparer une offre commerciale",
  'T 2.1': "choisir les matériels",
  'T 2.2': "réaliser les documents techniques du projet/chantier",
  'T 3.1': "proposer un protocole pour analyser le fonctionnement et/ou le comportement de l’installation",
  'T 3.2': "mesurer et contrôler l’installation, exploiter les mesures pour faire le diagnostic",
  'T 3.3': "formuler des préconisations",
  'T 4.1': "organiser la maintenance",
  'T 4.2': "réaliser la maintenance préventive ou prévisionnelle",
  'T 4.3': "réaliser la maintenance corrective",
  'T 5.1': "s’approprier et vérifier les informations relatives au projet/chantier",
  'T 5.2': "planifier les étapes du projet/chantier",
  'T 5.3': "assurer le suivi de la réalisation du projet/chantier (coûts, délais, qualité)",
  'T 5.4': "faire appliquer les règles liées à la santé, la sécurité et l’environnement",
  'T 5.5': "gérer et animer l’équipe projet/chantier",
  'T 6.1': "organiser l’espace de travail",
  'T 6.2': "implanter, poser, installer, câbler, raccorder les matériels électriques",
  'T 6.3': "programmer les applications métiers",
  'T 7.1': "réaliser les contrôles, les configurations, les essais fonctionnels",
  'T 7.2': "vérifier le fonctionnement de l’installation",
  'T 7.3': "réceptionner l’installation avec le client/utilisateur",
  'T 8.1': "constituer et mettre à jour les dossiers du projet/chantier",
  'T 8.2': "échanger, y compris en langue anglaise, avec les parties prenantes du projet/chantier",
  'T 8.3': "expliquer, y compris en langue anglaise, le fonctionnement de l’installation et former le client/utilisateur à son utilisation",
  'T 8.4': "préparer et animer des réunions",
  'T 8.5': "présenter et argumenter, y compris en langue anglaise, une offre à un client/utilisateur",
};

// Correspondances Tâche -> Compétences (extraites du référentiel)
const TASK_TO_COMPETENCIES = {
  'T 5.2': ['C1', 'C10'], 'T 5.4': ['C1', 'C3'],
  'T 3.1': ['C2'], 'T 4.1': ['C2'], 'T 4.2': ['C2', 'C13', 'C18'], 'T 4.3': ['C2', 'C13', 'C17', 'C18'],
  'T 5.3': ['C3'],
  'T 7.3': ['C4'], 'T 8.2': ['C4', 'C12'], 'T 8.3': ['C4'], 'T 8.4': ['C4'], 'T 8.5': ['C4', 'C5', 'C10'],
  'T 1.1': ['C5'], 'T 1.2': ['C5', 'C6', 'C8', 'C10'], 'T 1.3': ['C5', 'C6', 'C8'], 'T 1.4': ['C5'],
  'T 2.1': ['C7', 'C9'], 'T 2.2': ['C11'],
  'T 5.1': ['C12'], 'T 5.5': ['C12'],
  'T 3.2': ['C13', 'C17'], 'T 3.3': ['C17'],
  'T 6.1': ['C14'], 'T 6.2': ['C14'],
  'T 6.3': ['C15'], 'T 7.1': ['C15', 'C16'], 'T 7.2': ['C15', 'C16'],
  'T 8.1': ['C11'],
};

// Dérivé: Compétence -> Tâches
const COMPETENCE_TO_TASKS = Object.entries(TASK_TO_COMPETENCIES).reduce((acc, [task, comps]) => {
  comps.forEach((c) => {
    if (!acc[c]) acc[c] = [];
    acc[c].push(task);
  });
  // trier pour stabilité
  Object.values(acc).forEach((arr) => arr.sort((a, b) => a.localeCompare(b, 'fr')));
  return acc;
}, {});

// Distinct, accessible palette for teachers (cycled if > length)
const TEACHER_COLOR_PALETTE = [
  '#e53935', // red
  '#fb8c00', // orange
  '#43a047', // green
  '#1e88e5', // blue
  '#8e24aa', // purple
  '#00897b', // teal
  '#fdd835', // amber
  '#5e35b1', // deep purple
  '#d81b60', // pink
  '#3949ab', // indigo
  '#00acc1', // cyan
  '#7cb342', // light green
];

function buildTeacherColorMap(teachers = []) {
  const map = {};
  const ids = teachers.map((t) => String(t.id)).sort((a, b) => {
    // numeric sort when possible for stability
    const na = Number(a);
    const nb = Number(b);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
    return a.localeCompare(b);
  });
  ids.forEach((id, idx) => {
    map[id] = TEACHER_COLOR_PALETTE[idx % TEACHER_COLOR_PALETTE.length];
  });
  return map;
}

// Helpers
function toDate(value) {
  const d = new Date(value);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}
function inRange(day, start, end) {
  const x = toDate(day).getTime();
  return x >= toDate(start).getTime() && x <= toDate(end).getTime();
}
function formatShort(d) {
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

function getMonthName(date) {
  return new Date(date).toLocaleDateString('fr-FR', { month: 'short' });
}

// Fonction pour grouper les semaines par mois et calculer les colspan
function groupWeeksByMonth(weeks) {
  const groups = [];
  let currentMonth = null;
  let currentGroup = null;

  weeks.forEach((week, index) => {
    // Utiliser le mois de début de semaine comme mois principal
    const month = getMonthName(week.start);
    
    if (month !== currentMonth) {
      // Nouveau mois, créer un nouveau groupe
      if (currentGroup) {
        groups.push(currentGroup);
      }
      currentMonth = month;
      currentGroup = {
        month: month,
        startIndex: index,
        endIndex: index,
        count: 1
      };
    } else {
      // Même mois, étendre le groupe actuel
      currentGroup.endIndex = index;
      currentGroup.count++;
    }
  });

  // Ajouter le dernier groupe
  if (currentGroup) {
    groups.push(currentGroup);
  }

  return groups;
}

// Fonction pour comparer deux listes de sessions (pour fusion)
function sessionKey(session) {
  // Créer une clé unique pour identifier une session identique
  return JSON.stringify({
    label: session.label,
    type: session.type,
    livrable: session.livrable,
    prof: session.prof?.id,
    volumeSeances: session.volumeSeances,
    dureeSeanceH: session.dureeSeanceH,
    competences: Array.isArray(session.competences) ? [...session.competences].sort().join(',') : '',
    taches: Array.isArray(session.taches) ? [...session.taches].sort().join(',') : ''
  });
}

function sessionsEqual(list1, list2) {
  if (!Array.isArray(list1) || !Array.isArray(list2)) return list1 === list2;
  if (list1.length !== list2.length) return false;
  
  // Créer une représentation string pour comparer
  const normalize = (sessions) => {
    return sessions
      .map(s => sessionKey(s))
      .sort()
      .join('|');
  };
  
  return normalize(list1) === normalize(list2);
}

// Fonction pour calculer les groupes de fusion pour une matière
// Version simple : fusionne les semaines consécutives qui ont exactement le même contenu
function calculateMergedCells(sessionsByWeek) {
  const merged = [];
  let currentGroup = null;

  for (let weekIndex = 0; weekIndex < sessionsByWeek.length; weekIndex++) {
    const sessionsThisWeek = sessionsByWeek[weekIndex] || [];

    // Créer une clé pour représenter le contenu de cette semaine
    const contentKey = sessionsThisWeek
      .map((s) => sessionKey(s))
      .sort()
      .join('|');

    // Comparer avec le groupe précédent
    const prevContentKey = currentGroup?.contentKey;

    if (contentKey !== prevContentKey) {
      // Nouveau contenu, sauvegarder le groupe précédent
      if (currentGroup) {
        merged.push(currentGroup);
      }
      // Créer un nouveau groupe
      currentGroup = {
        startIndex: weekIndex,
        endIndex: weekIndex,
        count: 1,
        content: [...sessionsThisWeek],
        contentKey,
      };
    } else {
      // Même contenu, étendre le groupe
      currentGroup.endIndex = weekIndex;
      currentGroup.count++;
    }
  }

  // Ajouter le dernier groupe
  if (currentGroup) {
    merged.push(currentGroup);
  }

  return merged;
}

// Default seed data (editable in UI)
const DEFAULT_DATA = {
  calendrier: {
    annee: '2025-2026',
    dateDebut: '2025-08-18',
    dateFin: '2026-07-05',
    vacances: [
      { label: 'Vacances Toussaint', type: 'vacance', debut: '2025-10-20', fin: '2025-11-02' },
      { label: 'Vacances Noël', type: 'vacance', debut: '2025-12-22', fin: '2026-01-04' },
      { label: 'Vacances Hiver', type: 'vacance', debut: '2026-03-02', fin: '2026-03-15' },
    ],
    stages: [
      { label: 'Stage BTS1', type: 'stage', debut: '2026-05-04', fin: '2026-05-31', classes: ['BTS1'] },
    ],
    examens: [
      { label: 'CCF U52', type: 'examens', debut: '2026-02-16', fin: '2026-02-22' },
      { label: 'Écrits BTS', type: 'examens', debut: '2026-06-01', fin: '2026-06-14' },
    ],
    feries: [
      { label: '1er novembre', type: 'feries', debut: '2025-11-01', fin: '2025-11-01' },
      { label: '11 novembre', type: 'feries', debut: '2025-11-11', fin: '2025-11-11' },
      { label: 'Lundi de Pâques', type: 'feries', debut: '2026-04-06', fin: '2026-04-06' },
      { label: '1er mai', type: 'feries', debut: '2026-05-01', fin: '2026-05-01' },
      { label: '8 mai', type: 'feries', debut: '2026-05-08', fin: '2026-05-08' },
      { label: 'Ascension', type: 'feries', debut: '2026-05-14', fin: '2026-05-14' },
    ],
  },
  // Deuxième année (BTS2) — valeurs par défaut pour 2026-2027
  calendrier2: {
    annee: '2026-2027',
    dateDebut: '2026-08-17',
    dateFin: '2027-07-04',
    vacances: [
      { label: 'Vacances Toussaint', type: 'vacance', debut: '2026-10-26', fin: '2026-11-08' },
      { label: 'Vacances Noël', type: 'vacance', debut: '2026-12-21', fin: '2027-01-03' },
      { label: 'Vacances Hiver', type: 'vacance', debut: '2027-02-22', fin: '2027-03-07' },
    ],
    stages: [
      { label: 'Stage BTS2', type: 'stage', debut: '2027-04-26', fin: '2027-05-23', classes: ['BTS2'] },
    ],
    examens: [
      { label: 'CCF U52', type: 'examens', debut: '2027-02-15', fin: '2027-02-21' },
      { label: 'Écrits BTS', type: 'examens', debut: '2027-06-07', fin: '2027-06-20' },
    ],
    feries: [
      { label: '1er novembre', type: 'feries', debut: '2026-11-01', fin: '2026-11-01' },
      { label: '11 novembre', type: 'feries', debut: '2026-11-11', fin: '2026-11-11' },
      { label: 'Lundi de Pâques', type: 'feries', debut: '2027-03-29', fin: '2027-03-29' },
      { label: '1er mai', type: 'feries', debut: '2027-05-01', fin: '2027-05-01' },
      { label: '8 mai', type: 'feries', debut: '2027-05-08', fin: '2027-05-08' },
      { label: 'Ascension', type: 'feries', debut: '2027-05-13', fin: '2027-05-13' },
    ],
  },
  matieres: [],
  // Profs désormais chargés depuis /api/users (role=teacher)
  profs: [],
  templates: [
    { id: 'T1', libelle: 'Cours 2h', type: 'cours', dureeH: 2, livrable: '', tags: [] },
    { id: 'T2', libelle: 'TP 4h', type: 'tp', dureeH: 4, livrable: 'CR TP', tags: ['sécurité'] },
    { id: 'T3', libelle: 'Éval 1h', type: 'evaluation', dureeH: 1, livrable: 'Note + corrigé', tags: [] },
    { id: 'T4', libelle: 'Projet', type: 'projet', dureeH: 3, livrable: 'Jalon', tags: [] },
  ],
};

const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-3 py-2 rounded-md text-sm font-medium ${
      active ? 'bg-primary-100 text-primary-900' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
    }`}
  >
    {children}
  </button>
);

const Badge = ({ colorClass, children, title }) => (
  <span
    title={title}
    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}
    style={{ whiteSpace: 'nowrap' }}
  >
    {children}
  </span>
);

// Generic period editor (vacances, examens, fériés, stage)
const PeriodEditor = ({ title, items, onChange, type, showClasses = false }) => {
  const addItem = () => {
    const base = { label: '', type, debut: '', fin: '' };
    if (showClasses) Object.assign(base, { classes: [] });
    onChange([...(items || []), base]);
  };
  const updateItem = (idx, field, value) => {
    const next = (items || []).map((it, i) => (i === idx ? { ...it, [field]: value } : it));
    onChange(next);
  };
  const removeItem = (idx) => {
    const next = (items || []).filter((_, i) => i !== idx);
    onChange(next);
  };
  const classesToString = (arr) => (Array.isArray(arr) ? arr.join(', ') : (arr || ''));
  const stringToClasses = (str) =>
    str
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
        <button type="button" className="btn btn-primary" onClick={addItem}>
          Ajouter
        </button>
      </div>
      <div className="space-y-2">
        {(items || []).length === 0 && (
          <div className="text-xs text-gray-500">Aucune période. Cliquez sur Ajouter.</div>
        )}
        {(items || []).map((it, idx) => (
          <div
            key={idx}
            className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end border rounded-md p-2"
          >
            <div className="md:col-span-4">
              <label className="label">Libellé</label>
              <input
                className="input"
                value={it.label || ''}
                onChange={(e) => updateItem(idx, 'label', e.target.value)}
                placeholder="Ex: Vacances Noël / Stage BTS1"
              />
            </div>
            <div className="md:col-span-3">
              <label className="label">Début</label>
              <input
                type="date"
                className="input"
                value={it.debut || ''}
                onChange={(e) => updateItem(idx, 'debut', e.target.value)}
              />
            </div>
            <div className="md:col-span-3">
              <label className="label">Fin</label>
              <input
                type="date"
                className="input"
                value={it.fin || ''}
                onChange={(e) => updateItem(idx, 'fin', e.target.value)}
              />
            </div>
            {showClasses && (
              <div className="md:col-span-10">
                <label className="label">Classes concernées</label>
                <input
                  className="input"
                  placeholder="Ex: BTS1, BTS2"
                  value={classesToString(it.classes)}
                  onChange={(e) => updateItem(idx, 'classes', stringToClasses(e.target.value))}
                />
              </div>
            )}
            <div className={`${showClasses ? 'md:col-span-2' : 'md:col-span-2'}`}>
              <button
                type="button"
                className="btn w-full p-2 flex items-center justify-center"
                title="Supprimer"
                aria-label="Supprimer"
                onClick={() => removeItem(idx)}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Modal d'édition du calendrier (année)
const CalendarModal = ({ open, title, calendrier, onChangeCalendrier, onClose }) => {
  const [local, setLocal] = useState(calendrier);
  useEffect(() => {
    if (open) setLocal(calendrier);
  }, [open, calendrier]);
  if (!open) return null;
  const apply = () => {
    onChangeCalendrier(local);
    onClose();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-md shadow-lg w-full max-w-6xl max-h-[85vh] flex flex-col">
        <div className="p-3 border-b flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <div className="flex gap-2">
            <button className="btn" onClick={onClose}>Annuler</button>
            <button className="btn btn-primary" onClick={apply}>Valider</button>
          </div>
        </div>
        <div className="p-4 space-y-3 flex-1 overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Année scolaire</label>
              <input
                className="input"
                value={local.annee}
                onChange={(e) => setLocal({ ...local, annee: e.target.value })}
              />
            </div>
            <div />
            <div>
              <label className="label">Début</label>
              <input
                type="date"
                className="input"
                value={local.dateDebut}
                onChange={(e) => setLocal({ ...local, dateDebut: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Fin</label>
              <input
                type="date"
                className="input"
                value={local.dateFin}
                onChange={(e) => setLocal({ ...local, dateFin: e.target.value })}
              />
            </div>
          </div>

          <div className="mt-1">
            <div className="text-sm font-medium text-gray-700">Périodes</div>
            <div className="text-xs text-gray-500 mb-2">
              Ajoutez, modifiez ou supprimez les périodes qui bloquent l&apos;emploi du temps.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <PeriodEditor
                title="Vacances"
                type="vacance"
                items={local.vacances}
                onChange={(items) => setLocal({ ...local, vacances: items })}
              />
              <PeriodEditor
                title="Jours fériés"
                type="feries"
                items={local.feries}
                onChange={(items) => setLocal({ ...local, feries: items })}
              />
              <PeriodEditor
                title="Périodes de stage"
                type="stage"
                showClasses
                items={local.stages}
                onChange={(items) => setLocal({ ...local, stages: items })}
              />
              <PeriodEditor
                title="Examens"
                type="examens"
                items={local.examens}
                onChange={(items) => setLocal({ ...local, examens: items })}
              />
            </div>
          </div>
        </div>
        <div className="p-3 border-t flex items-center justify-end gap-2">
          <button className="btn" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={apply}>Valider</button>
        </div>
      </div>
    </div>
  );
};

// Modal de sélection de compétences
const CompetencesModal = ({ open, onClose, selectedCodes = [], onSave }) => {
  const [query, setQuery] = useState('');
  const [local, setLocal] = useState(new Set(selectedCodes));
  useEffect(() => {
    setLocal(new Set(selectedCodes));
  }, [selectedCodes, open]);

  if (!open) return null;
  const filtered = COMPETENCE_LIST.filter(
    (c) =>
      c.code.toLowerCase().includes(query.toLowerCase()) ||
      c.label.toLowerCase().includes(query.toLowerCase())
  );
  const toggle = (code) => {
    const next = new Set(local);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setLocal(next);
  };
  const save = () => onSave(Array.from(local));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-md shadow-lg w-full max-w-2xl">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Sélectionner des compétences</h3>
          <button className="btn" onClick={onClose}>Fermer</button>
        </div>
        <div className="p-4 flex items-center gap-2">
          <input
            className="input w-full"
            placeholder="Rechercher une compétence (code ou définition)…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="p-4 max-h-[60vh] overflow-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {filtered.map((c) => {
              const checked = local.has(c.code);
              return (
                <label key={c.code} className={`flex items-start gap-2 p-2 border rounded cursor-pointer ${checked ? 'bg-blue-50 border-blue-200' : ''}`} title={c.label}>
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={checked}
                    onChange={() => toggle(c.code)}
                  />
                  <div>
                    <div className="font-medium text-gray-900">{c.code}</div>
                    <div className="text-xs text-gray-600 line-clamp-2">{c.label}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
        <div className="p-4 border-t flex items-center justify-end gap-2">
          <button className="btn" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={save}>Valider</button>
        </div>
      </div>
    </div>
  );
};

// Modal d'ajout/édition d'une matière
const SubjectModal = ({ open, initial, onSave, onClose, weeks1 = [], weeks2 = [], teachers = [], selectedTeacherId = '', existingSubjects = [], fromPlan = false, defaultPeriod = null }) => {
  const TYPE_OPTIONS = [
    { value: 'cours', label: 'Cours' },
    { value: 'tp', label: 'TP' },
    { value: 'td', label: 'TD' },
    { value: 'cours_tp', label: 'Cours + TP' },
    { value: 'cours_td', label: 'Cours + TD' },
    { value: 'projet', label: 'Projet' },
    { value: 'sae', label: 'SAÉ' },
    { value: 'evaluation', label: 'Évaluation' },
    { value: 'ccf', label: 'CCF' },
  ];
  // Couleurs de fond par type pour les périodes dans le modal
  const TYPE_BG_CLASSES = {
    cours: 'bg-green-50',       // cours = vert clair
    tp: 'bg-orange-50',         // TP = orange clair
    td: 'bg-blue-50',           // TD = bleu clair
    cours_tp: 'bg-yellow-50',   // Cours + TP = jaune clair
    cours_td: 'bg-lime-50',     // Cours + TD = vert lime clair
    projet: 'bg-purple-50',     // projet = violet clair
    sae: 'bg-cyan-50',          // SAÉ = bleu/vert clair
    evaluation: 'bg-red-50',    // évaluation = rouge clair
    ccf: 'bg-pink-50',          // CCF = rose clair
  };
  const CLASSE_OPTIONS = ['BTS1', 'BTS2', 'BTS1+BTS2'];

  const defaults = {
    id: 'MX',
    code: '',
    nom: '',
    type: 'cours',
    classe: 'BTS1',
    volumeSeances: 1,
    dureeSeanceH: 1,
    competences: [],
    taches: [],
    placementPeriods: [],
  };

  const [local, setLocal] = useState(initial || defaults);
  const [showCompModal, setShowCompModal] = useState(false);
  const [teacherId, setTeacherId] = useState(selectedTeacherId || '');
  const [selectedSubjectId, setSelectedSubjectId] = useState(initial?.id || '');
  const [showMatiereList, setShowMatiereList] = useState(false);
  const [matiereSearch, setMatiereSearch] = useState(local.nom || '');
  const matiereInputRef = useRef(null);
  const matiereListRef = useRef(null);

  // Liste des matières du référentiel
  const REFERENTIEL_MATIERES = [
    'Culture générale et expression',
    'Langue vivante étrangère : anglais',
    'Mathématiques',
    'Physique chimie',
    'Sciences et techniques industrielles : STI',
    'STI en co-enseignement avec anglais',
    'STI en co-enseignement avec mathématiques',
    'Analyse, diagnostic, maintenance ( ADM)',
    'Accompagnement personnalisé',
    'Enseignement facultatif Langue vivante 2'
  ];

  // Filtrer les matières en fonction de la recherche
  const filteredMatieres = useMemo(() => {
    if (!matiereSearch.trim()) return REFERENTIEL_MATIERES;
    const searchLower = matiereSearch.toLowerCase();
    return REFERENTIEL_MATIERES.filter(matiere => 
      matiere.toLowerCase().includes(searchLower)
    );
  }, [matiereSearch]);

  // Gérer le clic en dehors de l'autocomplete
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        matiereInputRef.current && 
        !matiereInputRef.current.contains(event.target) &&
        matiereListRef.current && 
        !matiereListRef.current.contains(event.target)
      ) {
        setShowMatiereList(false);
      }
    };

    if (showMatiereList) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showMatiereList]);

  // Mettre à jour la recherche quand local.nom change
  useEffect(() => {
    setMatiereSearch(local.nom || '');
  }, [local.nom]);

  // Fonction helper pour convertir les périodes de l'ancien format au nouveau
  const convertPeriodToNewFormat = (period, defaultDureeSeanceH = 1, defaultType = 'cours') => {
    if (Array.isArray(period.contents) && period.contents.length > 0) {
      // Déjà au nouveau format, mais s'assurer que chaque contenu a un type
      const converted = { ...period };
      converted.contents = period.contents.map(c => ({
        ...c,
        type: c.type || period.type || defaultType
      }));
      return converted;
    }
    // Convertir l'ancien format
    const converted = { ...period };
    if (period.content || period.dureeSeanceH !== undefined) {
      converted.contents = [{
        content: period.content || '',
        dureeSeanceH: period.dureeSeanceH ?? defaultDureeSeanceH,
        type: period.type || defaultType
      }];
      delete converted.content;
      delete converted.dureeSeanceH;
    } else {
      converted.contents = [{ content: '', dureeSeanceH: defaultDureeSeanceH, type: period.type || defaultType }];
    }
    return converted;
  };

  useEffect(() => {
    if (open) {
      if (fromPlan && defaultPeriod) {
        // Si on vient du plan avec une période par défaut
        if (defaultPeriod.subject && defaultPeriod.session) {
          // Cas 1: On a cliqué sur une session existante - pré-remplir avec ses données
          const subject = defaultPeriod.subject;
          const session = defaultPeriod.session;
          
          // Trouver ou créer la période correspondant à la session cliquée
          const clickedPeriod = {
            start: defaultPeriod.weekStartIndex,
            end: defaultPeriod.weekEndIndex,
            teacherId: session.prof?.id,
            contents: [{
              content: session.label || '',
              dureeSeanceH: session.dureeSeanceH || subject.dureeSeanceH || 1
            }],
            type: session.type || subject.type || 'cours',
            volumeSeances: session.volumeSeances || subject.volumeSeances || 1
          };
          
          // Mettre à jour les périodes existantes ou en créer une nouvelle
          const existingPeriods = (subject.placementPeriods || []).filter(p => 
            !(p.start === clickedPeriod.start && p.end === clickedPeriod.end)
          );
          
          const updatedSubject = {
            ...subject,
            classe: defaultPeriod.classe || subject.classe || 'BTS1',
            placementPeriods: [...existingPeriods, clickedPeriod]
          };
          
          setLocal(updatedSubject);
          setSelectedSubjectId(subject.id || '');
          setTeacherId(session.prof?.id || selectedTeacherId || '');
        } else if (!initial?.id) {
          // Cas 2: Cellule vide - vérifier si une matière est spécifiée dans defaultPeriod ou déjà sélectionnée
          const subjectIdToLoad = defaultPeriod?.subjectId || selectedSubjectId;
          if (subjectIdToLoad && existingSubjects.length > 0) {
            // Charger la matière existante avec toutes ses périodes
            const existingSubject = existingSubjects.find(s => s.id === subjectIdToLoad);
            if (existingSubject) {
              const converted = { ...existingSubject };
              // Convertir les périodes existantes au nouveau format
              if (converted.placementPeriods && converted.placementPeriods.length > 0) {
                converted.placementPeriods =                 converted.placementPeriods.map(p => 
                  convertPeriodToNewFormat(p, converted.dureeSeanceH ?? 1, converted.type || 'cours')
                );
              } else {
                converted.placementPeriods = [];
              }
              // Ajouter la nouvelle période pour la semaine cliquée seulement si elle n'existe pas déjà
              if (defaultPeriod) {
                const weekStart = defaultPeriod.weekStartIndex;
                const weekEnd = defaultPeriod.weekEndIndex;
                // Vérifier si une période existe déjà pour cette semaine
                const periodExists = converted.placementPeriods.some(p => 
                  p.start === weekStart && p.end === weekEnd
                );
                if (!periodExists) {
                  const newPeriod = {
                    start: weekStart,
                    end: weekEnd,
                    teacherId: undefined,
                    contents: [{ content: '', dureeSeanceH: converted.dureeSeanceH ?? 1 }]
                  };
                  converted.placementPeriods = [...converted.placementPeriods, newPeriod];
                }
              }
              setLocal(converted);
              setSelectedSubjectId(subjectIdToLoad);
              setTeacherId(selectedTeacherId || '');
            } else {
              // Matière non trouvée, créer une nouvelle
              const newDefaults = {
                ...defaults,
                classe: defaultPeriod?.classe || 'BTS1',
                placementPeriods: defaultPeriod ? [{
                  start: defaultPeriod.weekStartIndex,
                  end: defaultPeriod.weekEndIndex,
                  teacherId: undefined,
                  contents: [{ content: '', dureeSeanceH: defaults.dureeSeanceH ?? 1 }]
                }] : []
              };
              setLocal(newDefaults);
              setSelectedSubjectId('');
              setTeacherId('');
            }
          } else {
            // Pas de matière sélectionnée, créer une nouvelle matière avec la période par défaut
            const newDefaults = {
              ...defaults,
              classe: defaultPeriod?.classe || 'BTS1',
              placementPeriods: defaultPeriod ? [{
                start: defaultPeriod.weekStartIndex,
                end: defaultPeriod.weekEndIndex,
                teacherId: undefined,
                contents: [{ content: '', dureeSeanceH: defaults.dureeSeanceH ?? 1 }]
              }] : []
            };
            setLocal(newDefaults);
            setSelectedSubjectId('');
            setTeacherId('');
          }
        } else {
          // Cas 3: Matière existante - charger avec toutes ses périodes
          const data = initial || defaults;
          const converted = { ...data };
          if (!converted.placementPeriods || converted.placementPeriods.length === 0) {
            if (data.weekStartIndex !== undefined || data.weekEndIndex !== undefined) {
              converted.placementPeriods = [{
                start: data.weekStartIndex,
                end: data.weekEndIndex,
                teacherId: undefined,
                contents: [{ content: '', dureeSeanceH: converted.dureeSeanceH ?? 1 }]
              }];
            } else {
              converted.placementPeriods = [];
            }
          } else {
            // Convertir les périodes existantes au nouveau format
            converted.placementPeriods =                 converted.placementPeriods.map(p => 
                  convertPeriodToNewFormat(p, converted.dureeSeanceH ?? 1, converted.type || 'cours')
                );
          }
          // Ne pas ajouter de nouvelle période automatiquement si on charge une matière existante
          setLocal(converted);
          setSelectedSubjectId(converted.id || '');
          setTeacherId(selectedTeacherId || '');
        }
      } else {
        // Cas normal (pas depuis le plan)
        const data = initial || defaults;
        const converted = { ...data };
        if (!converted.placementPeriods || converted.placementPeriods.length === 0) {
          if (data.weekStartIndex !== undefined || data.weekEndIndex !== undefined) {
            converted.placementPeriods = [{
              start: data.weekStartIndex,
              end: data.weekEndIndex,
              teacherId: undefined,
              contents: [{ content: '', dureeSeanceH: converted.dureeSeanceH ?? 1 }]
            }];
          } else {
            converted.placementPeriods = [];
          }
        } else {
          // Convertir les périodes existantes au nouveau format
          converted.placementPeriods =                 converted.placementPeriods.map(p => 
                  convertPeriodToNewFormat(p, converted.dureeSeanceH ?? 1, converted.type || 'cours')
                );
        }
        setLocal(converted);
        setSelectedSubjectId(converted.id || '');
        setTeacherId(selectedTeacherId || '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial?.id, selectedTeacherId, fromPlan, defaultPeriod]);

  if (!open) return null;

  const weeksForClasse = (classe) => {
    const c = String(classe || '').toUpperCase();
    if (c.includes('BTS2')) return weeks2 || [];
    return weeks1 || [];
  };

  const apply = () => {
    // Vérifier que soit l'intitulé soit au moins un contenu de période est rempli
    const hasIntitule = local.nom && local.nom.trim() !== '';
    const hasContent = (local.placementPeriods || []).some(period => {
      // Vérifier le nouveau format (contents array)
      if (Array.isArray(period.contents) && period.contents.length > 0) {
        return period.contents.some(c => c.content && c.content.trim() !== '');
      }
      // Vérifier l'ancien format (content)
      return period.content && period.content.trim() !== '';
    });
    
    if (!hasIntitule && !hasContent) {
      alert('Veuillez remplir au moins l\'intitulé de la matière ou un contenu/libellé dans une période de placement.');
      return;
    }
    
    onSave(local, teacherId);
    onClose();
  };

  const handleSubjectSelect = (subjectId) => {
    if (!subjectId) {
      // Créer une nouvelle matière
      setSelectedSubjectId('');
      const newDefaults = {
        ...defaults,
        classe: defaultPeriod?.classe || 'BTS1',
        placementPeriods: defaultPeriod ? [{
          start: defaultPeriod.weekStartIndex,
          end: defaultPeriod.weekEndIndex,
          teacherId: undefined,
          contents: [{ content: '', dureeSeanceH: defaults.dureeSeanceH ?? 1 }]
        }] : []
      };
      setLocal(newDefaults);
      setTeacherId('');
    } else {
      // Charger une matière existante
      const subject = existingSubjects.find(s => s.id === subjectId);
      if (subject) {
        const converted = { ...subject };
        // S'assurer que placementPeriods existe
        if (!converted.placementPeriods || converted.placementPeriods.length === 0) {
          if (subject.weekStartIndex !== undefined || subject.weekEndIndex !== undefined) {
            converted.placementPeriods = [{
              start: subject.weekStartIndex,
              end: subject.weekEndIndex,
              teacherId: undefined,
              contents: [{ content: '', dureeSeanceH: converted.dureeSeanceH ?? 1 }]
            }];
          } else {
            converted.placementPeriods = [];
          }
        } else {
          // Convertir les périodes existantes au nouveau format
          converted.placementPeriods =                 converted.placementPeriods.map(p => 
                  convertPeriodToNewFormat(p, converted.dureeSeanceH ?? 1, converted.type || 'cours')
                );
        }
        // Ajouter la nouvelle période si on vient du plan
        if (fromPlan && defaultPeriod) {
          const newPeriod = {
            start: defaultPeriod.weekStartIndex,
            end: defaultPeriod.weekEndIndex,
            teacherId: undefined,
            contents: [{ content: '', dureeSeanceH: converted.dureeSeanceH ?? 1 }]
          };
          converted.placementPeriods = [...converted.placementPeriods, newPeriod];
        }
        setLocal(converted);
        setSelectedSubjectId(subjectId);
        setTeacherId('');
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-md shadow-lg w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">{initial ? 'Modifier la matière' : 'Ajouter une matière'}</h3>
          <button className="btn" onClick={onClose}>Fermer</button>
        </div>
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {fromPlan && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <label className="label">Sélectionner une matière existante ou créer une nouvelle</label>
              <select
                className="input w-full"
                value={selectedSubjectId}
                onChange={(e) => handleSubjectSelect(e.target.value)}
              >
                <option value="">— Créer une nouvelle matière —</option>
                {existingSubjects.map((subj) => (
                  <option key={subj.id} value={subj.id}>
                    {subj.nom || subj.code || 'Sans intitulé'} ({subj.code || 'sans code'}) - {subj.classe || 'BTS1'}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-3">
              <label className="label">Code</label>
              <input className="input" value={local.code || ''} onChange={(e) => setLocal({ ...local, code: e.target.value })} disabled={!!selectedSubjectId && fromPlan} />
            </div>
            <div className="md:col-span-5 relative" ref={matiereInputRef}>
              <label className="label">Intitulé</label>
              <input
                className="input w-full"
                value={matiereSearch}
                onChange={(e) => {
                  setMatiereSearch(e.target.value);
                  setLocal({ ...local, nom: e.target.value });
                  setShowMatiereList(true);
                }}
                onFocus={() => setShowMatiereList(true)}
                disabled={!!selectedSubjectId && fromPlan}
                placeholder="Tapez ou sélectionnez une matière"
              />
              {showMatiereList && !(selectedSubjectId && fromPlan) && (
                <div
                  ref={matiereListRef}
                  className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
                >
                  {filteredMatieres.length > 0 ? (
                    filteredMatieres.map((matiere, idx) => (
                      <div
                        key={idx}
                        className="px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm"
                        onClick={() => {
                          setMatiereSearch(matiere);
                          setLocal({ ...local, nom: matiere });
                          setShowMatiereList(false);
                        }}
                      >
                        {matiere}
            </div>
                    ))
                  ) : (
                    <div
                      className="px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm text-blue-600 font-medium"
                      onClick={() => {
                        // Créer une nouvelle matière avec le texte tapé
                        setLocal({ ...local, nom: matiereSearch });
                        setShowMatiereList(false);
                      }}
                    >
                      ✓ Créer "{matiereSearch}"
                    </div>
                  )}
                  {matiereSearch && !filteredMatieres.some(m => m.toLowerCase() === matiereSearch.toLowerCase()) && (
                    <div
                      className="px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm text-blue-600 font-medium border-t"
                      onClick={() => {
                        // Créer une nouvelle matière avec le texte tapé
                        setLocal({ ...local, nom: matiereSearch });
                        setShowMatiereList(false);
                      }}
                    >
                      ✓ Créer "{matiereSearch}"
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="md:col-span-4">
              <label className="label">Classe</label>
              <select
                className="input"
                value={local.classe || 'BTS1'}
                onChange={(e) => setLocal({ ...local, classe: e.target.value })}
                disabled={!!selectedSubjectId && fromPlan}
              >
                {CLASSE_OPTIONS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-12">
              <label className="label">Périodes de placement</label>
              <div className="space-y-2">
                {(local.placementPeriods || []).length === 0 && (
                  <div className="text-xs text-gray-500 mb-2">
                    Aucune période définie. Le placement commencera le plus tôt possible et s’étendra jusqu’à la fin de l’année.
                  </div>
                )}
                       {(local.placementPeriods || []).map((period, idx) => {
                         // Utiliser le type du premier contenu s'il existe, sinon le type de la période, sinon le type par défaut
                         const firstContentType = period.contents?.[0]?.type;
                         const pType = firstContentType || period.type || local.type || 'cours';
                         const bgClass = TYPE_BG_CLASSES[pType] || 'bg-gray-50';
                         return (
                         <div
                           key={idx}
                           className={`border rounded-md p-3 space-y-2 ${bgClass}`}
                         >
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                      <div className="md:col-span-4">
                          <span className="text-xs text-gray-600">Début</span>
                          <select
                          className="input w-full text-sm"
                            value={Number.isInteger(period.start) ? period.start : ''}
                            onChange={(e) => {
                            const updated = [...(local.placementPeriods || [])];
                            updated[idx] = {
                              ...updated[idx],
                              start: e.target.value === '' ? undefined : Number(e.target.value)
                            };
                            setLocal({ ...local, placementPeriods: updated });
                          }}
                          style={{ minWidth: '180px' }}
                          >
                            <option value="">— Non défini —</option>
                            {weeksForClasse(local.classe).map((w, i) => (
                              <option key={i} value={i}>{w.label}</option>
                            ))}
                          </select>
                        </div>
                      <div className="md:col-span-3">
                          <span className="text-xs text-gray-600">Fin</span>
                          <select
                          className="input w-full text-sm"
                            value={Number.isInteger(period.end) ? period.end : ''}
                            onChange={(e) => {
                            const updated = [...(local.placementPeriods || [])];
                            updated[idx] = {
                              ...updated[idx],
                              end: e.target.value === '' ? undefined : Number(e.target.value)
                            };
                            setLocal({ ...local, placementPeriods: updated });
                          }}
                          style={{ minWidth: '180px' }}
                          >
                            <option value="">— Non défini —</option>
                            {weeksForClasse(local.classe).map((w, i) => (
                              <option key={i} value={i}>{w.label}</option>
                            ))}
                          </select>
                        </div>
                      <div className="md:col-span-2">
                        <span className="text-xs text-gray-600">Séances</span>
                        <input
                          type="number"
                          min="0"
                          className="input w-full text-sm"
                          value={period.volumeSeances ?? local.volumeSeances ?? 0}
                            onChange={(e) => {
                            const updated = [...(local.placementPeriods || [])];
                            updated[idx] = {
                              ...updated[idx],
                              volumeSeances: e.target.value === '' ? undefined : Number(e.target.value)
                            };
                            setLocal({ ...local, placementPeriods: updated });
                          }}
                        />
                        </div>
                      <div className="md:col-span-1 flex items-end">
                        <button
                          type="button"
                          className="btn w-full p-2 flex items-center justify-center"
                          title="Supprimer"
                          onClick={() => {
                            if (window.confirm('Êtes-vous sûr de vouloir supprimer cette période de placement ?')) {
                              const updated = (local.placementPeriods || []).filter((_, i) => i !== idx);
                              setLocal({ ...local, placementPeriods: updated });
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        </div>
                      </div>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                      <div className="md:col-span-6">
                        <span className="text-xs text-gray-600">Professeur (optionnel)</span>
                        <select
                          className="input w-full text-sm"
                          value={period.teacherId || ''}
                            onChange={(e) => {
                            const updated = [...(local.placementPeriods || [])];
                            updated[idx] = {
                              ...updated[idx],
                              teacherId: e.target.value === '' ? undefined : e.target.value
                            };
                            setLocal({ ...local, placementPeriods: updated });
                          }}
                          style={{ minWidth: '200px' }}
                        >
                          <option value="">— Utiliser le professeur principal —</option>
                          {teachers.map((t) => {
                            const display = (t.first_name && t.last_name) ? `${t.first_name} ${t.last_name}` : t.username;
                            return (
                              <option key={t.id} value={t.id}>{display}</option>
                            );
                          })}
                        </select>
                        </div>
                    </div>
                    
                    {/* Contenus avec durées */}
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-600 font-medium">Contenu/Libellé et Durée</span>
                        <button
                          type="button"
                          className="btn btn-xs flex items-center gap-1"
                          onClick={() => {
                            const updated = [...(local.placementPeriods || [])];
                            const currentContents = updated[idx].contents || [];
                            // Si on a un ancien content/dureeSeanceH, le convertir
                            if (!currentContents.length && (updated[idx].content || updated[idx].dureeSeanceH)) {
                              currentContents.push({
                                content: updated[idx].content || '',
                                dureeSeanceH: updated[idx].dureeSeanceH ?? local.dureeSeanceH ?? 1,
                                type: updated[idx].type || period.type || local.type || 'cours'
                              });
                              delete updated[idx].content;
                              delete updated[idx].dureeSeanceH;
                            }
                            currentContents.push({ content: '', dureeSeanceH: local.dureeSeanceH ?? 1, type: period.type || local.type || 'cours' });
                            updated[idx] = {
                              ...updated[idx],
                              contents: currentContents
                            };
                            setLocal({ ...local, placementPeriods: updated });
                          }}
                        >
                          <Plus className="w-3 h-3" /> Ajouter
                        </button>
                      </div>
                      {(() => {
                        // Convertir l'ancien format (content/dureeSeanceH) en nouveau format (contents array)
                        let contents = period.contents || [];
                        if (!contents.length && (period.content || period.dureeSeanceH !== undefined)) {
                          contents = [{
                            content: period.content || '',
                            dureeSeanceH: period.dureeSeanceH ?? local.dureeSeanceH ?? 1,
                            type: period.type || local.type || 'cours'
                          }];
                        }
                        if (!contents.length) {
                          contents = [{ content: '', dureeSeanceH: local.dureeSeanceH ?? 1, type: period.type || local.type || 'cours' }];
                        }
                        return contents.map((contentItem, contentIdx) => {
                          const contentKey = `${idx}-${contentIdx}`;
                          return (
                            <div key={contentKey} className="flex gap-2 items-end">
                              <div className="flex-1">
                                <input
                                  type="text"
                                  className="input w-full text-sm"
                                  placeholder="Ex: Phase 1, Partie théorique, etc."
                                  value={contentItem.content || ''}
                                  onChange={(e) => {
                                    const updated = [...(local.placementPeriods || [])];
                                    const currentContents = updated[idx].contents || [];
                                    if (!currentContents.length && (updated[idx].content || updated[idx].dureeSeanceH !== undefined)) {
                                      currentContents.push({
                                        content: updated[idx].content || '',
                                        dureeSeanceH: updated[idx].dureeSeanceH ?? local.dureeSeanceH ?? 1
                                      });
                                      delete updated[idx].content;
                                      delete updated[idx].dureeSeanceH;
                                    }
                                    if (!currentContents[contentIdx]) {
                                      currentContents[contentIdx] = { content: '', dureeSeanceH: local.dureeSeanceH ?? 1, type: period.type || local.type || 'cours' };
                                    }
                                    currentContents[contentIdx].content = e.target.value || '';
                                    updated[idx] = {
                                      ...updated[idx],
                                      contents: currentContents
                                    };
                                    setLocal({ ...local, placementPeriods: updated });
                                  }}
                                />
                              </div>
                              <div className="flex gap-1 items-center" style={{ minWidth: '320px' }}>
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    min="0"
                                    className="input text-sm"
                                    style={{ width: '60px' }}
                                    placeholder="h"
                                    value={(() => {
                                      const dureeH = contentItem.dureeSeanceH ?? local.dureeSeanceH ?? 1;
                                      return Math.floor(dureeH);
                                    })()}
                                    onChange={(e) => {
                                      const updated = [...(local.placementPeriods || [])];
                                      const currentContents = updated[idx].contents || [];
                                      if (!currentContents.length && (updated[idx].content || updated[idx].dureeSeanceH !== undefined)) {
                                        currentContents.push({
                                          content: updated[idx].content || '',
                                          dureeSeanceH: updated[idx].dureeSeanceH ?? local.dureeSeanceH ?? 1
                                        });
                                        delete updated[idx].content;
                                        delete updated[idx].dureeSeanceH;
                                      }
                                      if (!currentContents[contentIdx]) {
                                        currentContents[contentIdx] = { content: '', dureeSeanceH: local.dureeSeanceH ?? 1, type: period.type || local.type || 'cours' };
                                      }
                                      
                                      const heures = parseInt(e.target.value) || 0;
                                      const dureeH = contentItem.dureeSeanceH ?? local.dureeSeanceH ?? 1;
                                      const minutesActuelles = Math.round((dureeH - Math.floor(dureeH)) * 60);
                                      const nouvelleDureeH = heures + (minutesActuelles / 60);
                                      
                                      currentContents[contentIdx].dureeSeanceH = nouvelleDureeH;
                                      
                                      updated[idx] = {
                                        ...updated[idx],
                                        contents: currentContents
                                      };
                                      setLocal({ ...local, placementPeriods: updated });
                                    }}
                                  />
                                  <span className="text-xs text-gray-600">h</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    min="0"
                                    max="59"
                                    className="input text-sm"
                                    style={{ width: '60px' }}
                                    placeholder="min"
                                    value={(() => {
                                      const dureeH = contentItem.dureeSeanceH ?? local.dureeSeanceH ?? 1;
                                      return Math.round((dureeH - Math.floor(dureeH)) * 60);
                                    })()}
                                    onChange={(e) => {
                                      const updated = [...(local.placementPeriods || [])];
                                      const currentContents = updated[idx].contents || [];
                                      if (!currentContents.length && (updated[idx].content || updated[idx].dureeSeanceH !== undefined)) {
                                        currentContents.push({
                                          content: updated[idx].content || '',
                                          dureeSeanceH: updated[idx].dureeSeanceH ?? local.dureeSeanceH ?? 1
                                        });
                                        delete updated[idx].content;
                                        delete updated[idx].dureeSeanceH;
                                      }
                                      if (!currentContents[contentIdx]) {
                                        currentContents[contentIdx] = { content: '', dureeSeanceH: local.dureeSeanceH ?? 1, type: period.type || local.type || 'cours' };
                                      }
                                      
                                      const minutes = parseInt(e.target.value) || 0;
                                      const dureeH = contentItem.dureeSeanceH ?? local.dureeSeanceH ?? 1;
                                      const heures = Math.floor(dureeH);
                                      const nouvelleDureeH = heures + (Math.min(59, Math.max(0, minutes)) / 60);
                                      
                                      currentContents[contentIdx].dureeSeanceH = nouvelleDureeH;
                                      
                                      updated[idx] = {
                                        ...updated[idx],
                                        contents: currentContents
                                      };
                                      setLocal({ ...local, placementPeriods: updated });
                                    }}
                                  />
                                  <span className="text-xs text-gray-600">min</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <select
                                    className="input text-sm"
                                    style={{ minWidth: '120px' }}
                                    value={contentItem.type || period.type || local.type || 'cours'}
                                    onChange={(e) => {
                                      const updated = [...(local.placementPeriods || [])];
                                      const currentContents = updated[idx].contents || [];
                                      if (!currentContents.length && (updated[idx].content || updated[idx].dureeSeanceH !== undefined)) {
                                        currentContents.push({
                                          content: updated[idx].content || '',
                                          dureeSeanceH: updated[idx].dureeSeanceH ?? local.dureeSeanceH ?? 1
                                        });
                                        delete updated[idx].content;
                                        delete updated[idx].dureeSeanceH;
                                      }
                                      if (!currentContents[contentIdx]) {
                                        currentContents[contentIdx] = { content: '', dureeSeanceH: local.dureeSeanceH ?? 1, type: period.type || local.type || 'cours' };
                                      }
                                      currentContents[contentIdx].type = e.target.value;
                                      updated[idx] = {
                                        ...updated[idx],
                                        contents: currentContents
                                      };
                                      setLocal({ ...local, placementPeriods: updated });
                                    }}
                                  >
                                    {TYPE_OPTIONS.map((o) => (
                                      <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                              {contents.length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-xs p-2"
                                  title="Supprimer"
                                  onClick={() => {
                                    const updated = [...(local.placementPeriods || [])];
                                    const currentContents = updated[idx].contents || [];
                                    currentContents.splice(contentIdx, 1);
                                    updated[idx] = {
                                      ...updated[idx],
                                      contents: currentContents.length > 0 ? currentContents : undefined
                                    };
                                    setLocal({ ...local, placementPeriods: updated });
                                  }}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>

                    {/* Compétences / tâches au niveau de la période */}
                    <div className="mt-2 border rounded-md p-2 bg-gray-50 space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-700">
                          Compétences / tâches pour cette période
                        </span>
                        <button
                          type="button"
                          className="btn btn-xs"
                          onClick={() => {
                            setShowCompModal(true);
                            setLocal((prev) => ({
                              ...prev,
                              _periodCompIndex: idx,
                            }));
                          }}
                        >
                          Ajouter / Modifier
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {Array.isArray(period.competences) && period.competences.length > 0 ? (
                          period.competences.map((code) => (
                            <span
                              key={code}
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-800 border"
                              title={COMPETENCE_LABELS[code] || 'Compétence'}
                            >
                              {code}
                            </span>
                          ))
                        ) : (
                          <span className="text-[11px] text-gray-400">
                            Aucune compétence pour cette période.
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {Array.isArray(period.taches) && period.taches.length > 0 &&
                          period.taches.map((tCode) => (
                            <span
                              key={tCode}
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-blue-50 text-blue-900 border border-blue-200"
                              title={TASK_LABELS[tCode] || 'Tâche'}
                            >
                              {tCode}
                            </span>
                          ))}
                      </div>
                    </div>
                  </div>
                )})}
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    const updated = [...(local.placementPeriods || []), { 
                      start: undefined, 
                      end: undefined, 
                      teacherId: undefined, 
                      contents: [{ content: '', dureeSeanceH: local.dureeSeanceH ?? 1 }],
                      volumeSeances: undefined, 
                      type: undefined 
                    }];
                    setLocal({ ...local, placementPeriods: updated });
                  }}
                >
                  <Plus className="w-4 h-4" /> Ajouter une période
                </button>
              </div>
            </div>
          </div>

          {/* Compétences globales supprimées : elles sont maintenant définies au niveau de chaque période */}
        </div>
        <div className="p-4 border-t flex items-center justify-end gap-2">
          <button className="btn" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={apply}>Valider</button>
        </div>
      </div>

      <CompetencesModal
        open={showCompModal}
        onClose={() => setShowCompModal(false)}
        selectedCodes={
          Number.isInteger(local._periodCompIndex) &&
          local.placementPeriods?.[local._periodCompIndex]?.competences
            ? local.placementPeriods[local._periodCompIndex].competences
            : []
        }
        onSave={(codes) => {
          const tasksSet = new Set();
          codes.forEach((c) => (COMPETENCE_TO_TASKS[c] || []).forEach((t) => tasksSet.add(t)));
          const tasks = Array.from(tasksSet).sort((a, b) => a.localeCompare(b, 'fr'));
          setLocal((prev) => {
            const idx = prev._periodCompIndex;
            if (!Number.isInteger(idx)) return prev;
            const periods = [...(prev.placementPeriods || [])];
            const target = periods[idx] || {};
            periods[idx] = {
              ...target,
              competences: codes,
              taches: tasks,
            };
            return {
              ...prev,
              placementPeriods: periods,
            };
          });
          setShowCompModal(false);
        }}
      />
    </div>
  );
};

// Editor for subjects (Matières / Modules)
const SubjectEditor = ({ items, onChange, weeks1, weeks2, teachers = [], affectations = {}, onChangeAffectations = () => {} }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editingTeacherId, setEditingTeacherId] = useState('');

  const nextId = () => {
    const nums = (items || [])
      .map((m) => (typeof m.id === 'string' && m.id.startsWith('M') ? Number(m.id.slice(1)) : 0))
      .filter((n) => !Number.isNaN(n));
    const max = nums.length ? Math.max(...nums) : 0;
    return `M${max + 1}`;
  };

  const weeksForClasse = (classe) => {
    const c = String(classe || '').toUpperCase();
    if (c.includes('BTS2')) return weeks2 || [];
    return weeks1 || [];
  };

  const weekLabel = (classe, idx) => {
    if (!Number.isInteger(idx)) return '— Non défini —';
    const arr = weeksForClasse(classe);
    return arr[idx]?.label || `S${idx + 1}`;
  };

  const openAdd = () => {
    const id = nextId();
    setDraft({
      id,
      code: '',
      nom: '',
      type: 'cours',
      classe: 'BTS1',
      volumeSeances: 1,
      dureeSeanceH: 1,
      competences: [],
      taches: [],
    });
    setEditingIndex(null);
    setEditingTeacherId('');
    setIsModalOpen(true);
  };

  const openEdit = (idx) => {
    setDraft(items[idx]);
    setEditingIndex(idx);
    const id = items[idx]?.id;
    setEditingTeacherId(affectations?.[id] || '');
    setIsModalOpen(true);
  };

  const remove = (idx) => {
    const next = (items || []).filter((_, i) => i !== idx);
    onChange(next);
  };

  const saveDraft = (subject, teacherId) => {
    if (editingIndex === null || editingIndex === undefined) {
      onChange([...(items || []), subject]);
    } else {
      const next = (items || []).map((m, i) => (i === editingIndex ? subject : m));
      onChange(next);
    }
    // mettre à jour l'affectation professeur
    const nextAff = { ...(affectations || {}) };
    if (teacherId) nextAff[subject.id] = teacherId;
    else delete nextAff[subject.id];
    onChangeAffectations(nextAff);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-800">Matières / Modules</h3>
        <button type="button" className="btn btn-primary flex items-center gap-2" onClick={openAdd}>
          <Plus className="w-4 h-4" />
          Ajouter une matière
        </button>
      </div>

      {(items || []).length === 0 && (
        <div className="text-xs text-gray-500">Aucune matière. Cliquez sur "Ajouter une matière".</div>
      )}

      <div className="space-y-2">
        {(items || []).map((m, idx) => (
          <div key={m.id || idx} className="p-3 border rounded-md">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-medium text-gray-900 truncate">{m.nom || '(Sans intitulé)'}</div>
                  <Badge colorClass={CATEGORY_COLORS[m.type] || CATEGORY_COLORS.cours}>
                    {(m.type || '').toString().toUpperCase()}
                  </Badge>
                </div>
                <div className="text-xs text-gray-600 truncate">
                  Code: <span className="font-mono">{m.code || '—'}</span> • Classe: {(m.classe || '').toString().toUpperCase()} • Séances: {m.volumeSeances ?? 0} × {formatDuree(m.dureeSeanceH ?? 1)}
                </div>
                <div className="text-[11px] text-gray-500">
                  Fenêtre: {weekLabel(m.classe, m.weekStartIndex)} → {weekLabel(m.classe, m.weekEndIndex)}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button className="btn p-2" title="Modifier" aria-label="Modifier" onClick={() => openEdit(idx)}>
                  <Pencil className="w-4 h-4" />
                </button>
                <button className="btn p-2" title="Supprimer" aria-label="Supprimer" onClick={() => remove(idx)}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <SubjectModal
        open={isModalOpen}
        initial={draft}
        onClose={() => setIsModalOpen(false)}
        onSave={saveDraft}
        selectedTeacherId={editingTeacherId}
        teachers={teachers}
        weeks1={weeks1}
        weeks2={weeks2}
      />
    </div>
  );
};

function generateWeeks(dateStart, dateEnd) {
  const start = toDate(dateStart);
  const end = toDate(dateEnd);
  const weeks = [];
  // Align to Monday
  const cursor = new Date(start);
  const day = cursor.getDay(); // 0 Sun - 6 Sat
  const offsetToMonday = ((day + 6) % 7);
  cursor.setDate(cursor.getDate() - offsetToMonday);
  let index = 1;
  while (cursor <= end) {
    const startOfWeek = new Date(cursor);
    const endOfWeek = addDays(startOfWeek, 6);
    const startMonth = getMonthName(startOfWeek);
    const endMonth = getMonthName(endOfWeek);
    // Si la semaine chevauche deux mois, afficher les deux mois
    const monthLabel = startMonth === endMonth ? startMonth : `${startMonth}/${endMonth}`;
    weeks.push({
      index,
      start: startOfWeek,
      end: endOfWeek,
      label: `S${index} (${formatShort(startOfWeek)}–${formatShort(endOfWeek)})`,
      monthLabel: monthLabel,
    });
    cursor.setDate(cursor.getDate() + 7);
    index += 1;
  }
  return weeks;
}

function buildBlockedBands(weeks, calendrier) {
  const bands = weeks.map(() => ({ types: [] }));
  const pushBand = (range, type, label) => {
    weeks.forEach((w, idx) => {
      if (inRange(w.start, range.debut, range.fin) || inRange(w.end, range.debut, range.fin) || (w.start <= toDate(range.debut) && w.end >= toDate(range.fin))) {
        bands[idx].types.push({ type, label });
      }
    });
  };
  calendrier.vacances.forEach(v => pushBand(v, 'vacance', v.label));
  calendrier.feries.forEach(f => pushBand(f, 'feries', f.label));
  calendrier.stages.forEach(s => pushBand(s, 'stage', s.label));
  calendrier.examens.forEach(e => pushBand(e, 'examens', e.label));
  return bands;
}

function autoGeneratePlan(weeks, bands, data, teachers = []) {
  const sessionsBySubject = {};
  const sessionsByProf = {};
  const evalPressureByWeek = Array.from({ length: weeks.length }, () => 0);

  // Build quick teacher map
  const teacherById = {};
  teachers.forEach((t) => { teacherById[String(t.id)] = t; });
  const teacherColorById = buildTeacherColorMap(teachers);

  const isBlockedWeek = (idx) => {
    const t = bands[idx]?.types?.map(x => x.type) || [];
    // Pour le placement des contenus du plan de formation, on ne tient pas compte
    // des vacances ni des jours fériés. On considère seulement les semaines
    // bloquées par les stages ou les examens.
    return t.includes('stage') || t.includes('examens');
  };

  const getColor = (type) => CATEGORY_COLORS[type] || CATEGORY_COLORS.cours;

  const subjectsOrdered = [...data.matieres];

  subjectsOrdered.forEach((matiere) => {
    // Utiliser le volume de séances par défaut de la matière
    const defaultVolumeSeances = matiere.volumeSeances || 1;
    const defaultDureeSeanceH = matiere.dureeSeanceH || 1;
    let remaining = defaultVolumeSeances;
    const subjectId = matiere.id;
    sessionsBySubject[subjectId] = {};

    // Resolve main professor from affectations mapping
    const teacherId = data.affectations?.[subjectId];
    const t = teacherById[String(teacherId)];
    const prof = t
      ? {
          id: t.id,
          nom: (t.first_name && t.last_name) ? `${t.first_name} ${t.last_name}` : (t.username || 'Prof'),
          initiales: initialsFromUser(t),
          couleur: teacherColorById[String(t.id)] || colorFromId(t.id),
        }
      : null;

    // Gérer les périodes de placement (nouveau système) ou weekStartIndex/weekEndIndex (ancien système)
    const placementPeriods = Array.isArray(matiere.placementPeriods) && matiere.placementPeriods.length > 0
      ? matiere.placementPeriods
      : (Number.isInteger(matiere.weekStartIndex) || Number.isInteger(matiere.weekEndIndex))
        ? [{ start: matiere.weekStartIndex, end: matiere.weekEndIndex }]
        : [];

    // Si des périodes sont définies, placer les sessions dans ces périodes
    if (placementPeriods.length > 0) {
      placementPeriods.forEach((period) => {
        const hasStart = Number.isInteger(period.start);
        const hasEnd = Number.isInteger(period.end);
        let startIdx = hasStart ? Math.max(0, Math.min(weeks.length - 1, Number(period.start))) : 0;
        let endIdx = hasEnd ? Math.max(0, Math.min(weeks.length - 1, Number(period.end))) : weeks.length - 1;
        if (endIdx < startIdx) {
          const tmp = startIdx;
          startIdx = endIdx;
          endIdx = tmp;
        }

        // Utiliser le professeur de la période si défini, sinon le professeur principal
        const periodTeacherId = period.teacherId || teacherId;
        const periodTeacher = periodTeacherId ? teacherById[String(periodTeacherId)] : null;
        const periodProf = periodTeacher
          ? {
              id: periodTeacher.id,
              nom: (periodTeacher.first_name && periodTeacher.last_name) ? `${periodTeacher.first_name} ${periodTeacher.last_name}` : (periodTeacher.username || 'Prof'),
              initiales: initialsFromUser(periodTeacher),
              couleur: teacherColorById[String(periodTeacher.id)] || colorFromId(periodTeacher.id),
            }
          : prof;
        
        // Utiliser les séances de la période si définies, sinon les valeurs par défaut
        const periodVolumeSeances = period.volumeSeances ?? defaultVolumeSeances;
        
        // Gérer les contenus : nouveau format (contents array) ou ancien format (content/dureeSeanceH)
        let contents = period.contents || [];
        if (!contents.length && (period.content || period.dureeSeanceH !== undefined)) {
          // Convertir l'ancien format
          contents = [{
            content: period.content || '',
            dureeSeanceH: period.dureeSeanceH ?? defaultDureeSeanceH,
            type: period.type || matiere.type || 'cours'
          }];
        }
        if (!contents.length) {
          // Pas de contenu défini, utiliser les valeurs par défaut
          contents = [{ content: '', dureeSeanceH: defaultDureeSeanceH, type: period.type || matiere.type || 'cours' }];
        }
        
        // Utiliser les compétences et tâches de la période si définies, sinon celles de la matière
        const periodCompetences = Array.isArray(period.competences) && period.competences.length > 0
          ? period.competences
          : (Array.isArray(matiere.competences) ? matiere.competences : []);
        const periodTaches = Array.isArray(period.taches) && period.taches.length > 0
          ? period.taches
          : (Array.isArray(matiere.taches) ? matiere.taches : []);
        
        // Créer une session pour chaque contenu
        contents.forEach((contentItem) => {
          const contentDureeSeanceH = contentItem.dureeSeanceH ?? defaultDureeSeanceH;
          // Utiliser le type du contenu, sinon le type de la période, sinon le type de la matière, sinon 'cours'
          const contentType = contentItem.type || period.type || matiere.type || 'cours';
          
          // Préparer le label de la session
          const baseLabel = contentItem.content && contentItem.content.trim()
            ? contentItem.content
            : (contentType === 'evaluation'
                ? `Éval ${matiere.nom || matiere.code}`
                : contentType === 'tp'
                ? `TP ${matiere.nom || matiere.code}`
                : contentType === 'td'
                ? `TD ${matiere.nom || matiere.code}`
                : contentType === 'cours_tp'
                ? `Cours + TP ${matiere.nom || matiere.code}`
                : contentType === 'cours_td'
                ? `Cours + TD ${matiere.nom || matiere.code}`
                : contentType === 'projet'
                ? `Projet ${matiere.nom || matiere.code}`
                : contentType === 'ccf'
                ? `CCF ${matiere.nom || matiere.code}`
                : matiere.nom || matiere.code);
          
          const sessionData = {
            label: baseLabel,
            type: contentType,
            livrable:
              contentType === 'tp' ? 'CR TP' : contentType === 'td' ? 'CR TD' : contentType === 'cours_tp' ? 'CR TP' : contentType === 'cours_td' ? 'CR TD' : contentType === 'evaluation' ? 'Note + corrigé' : contentType === 'ccf' ? 'Note + corrigé' : '',
            prof: periodProf,
            colorClass: getColor(contentType),
            competences: periodCompetences,
            taches: periodTaches,
            volumeSeances: periodVolumeSeances,
            dureeSeanceH: contentDureeSeanceH,
          };

          // Placer une session identique dans chaque semaine de la période pour permettre la fusion
          for (let w = startIdx; w <= endIdx && w < weeks.length; w++) {
            if (isBlockedWeek(w)) continue;
            if ((contentType === 'evaluation' || contentType === 'ccf') && evalPressureByWeek[w] >= 2) continue;

            const cell = sessionsBySubject[subjectId][w] || [];
            // Placer la même session dans chaque semaine pour permettre la fusion
            cell.push({ ...sessionData });
            sessionsBySubject[subjectId][w] = cell;
            if (contentType === 'evaluation' || contentType === 'ccf') evalPressureByWeek[w] += 1;
          }
        });
      });
    } else {
      // Otherwise, place sessions sequentially from the start of the year
      let startIdx = 0;
      for (let w = startIdx; w < weeks.length && remaining > 0; w++) {
        if (isBlockedWeek(w)) continue;
        if ((matiere.type === 'evaluation' || matiere.type === 'ccf') && evalPressureByWeek[w] >= 2) continue;

        const cell = sessionsBySubject[subjectId][w] || [];
        const sessionLabel =
          matiere.type === 'evaluation'
            ? `Éval ${matiere.code}`
            : matiere.type === 'tp'
            ? `TP ${matiere.code}`
            : matiere.type === 'td'
            ? `TD ${matiere.code}`
            : matiere.type === 'cours_tp'
            ? `Cours + TP ${matiere.code}`
            : matiere.type === 'cours_td'
            ? `Cours + TD ${matiere.code}`
            : matiere.type === 'projet'
            ? `Projet ${matiere.code}`
            : matiere.type === 'ccf'
            ? `CCF ${matiere.code}`
            : `Cours ${matiere.code}`;

        cell.push({
          label: sessionLabel,
          type: matiere.type,
          livrable:
            matiere.type === 'tp' ? 'CR TP' : matiere.type === 'td' ? 'CR TD' : matiere.type === 'cours_tp' ? 'CR TP' : matiere.type === 'cours_td' ? 'CR TD' : matiere.type === 'evaluation' ? 'Note + corrigé' : matiere.type === 'ccf' ? 'Note + corrigé' : '',
          prof,
          colorClass: getColor(matiere.type),
          competences: Array.isArray(matiere.competences) ? [...matiere.competences] : [],
          taches: Array.isArray(matiere.taches) ? [...matiere.taches] : [],
        });

        sessionsBySubject[subjectId][w] = cell;
        if (matiere.type === 'evaluation' || matiere.type === 'ccf') evalPressureByWeek[w] += 1;
        remaining -= 1;
      }
    }
  });

  // Build tasks by prof (keyed by teacher id)
  Object.values(sessionsBySubject).forEach((byWeek) => {
    Object.entries(byWeek).forEach(([wIdxStr, list]) => {
      const wIdx = Number(wIdxStr);
      list.forEach((s) => {
        if (!s.prof) return;
        const key = String(s.prof.id);
        if (!sessionsByProf[key]) sessionsByProf[key] = [];
        sessionsByProf[key].push({
          weekIndex: wIdx,
          weekLabel: weeks[wIdx].label,
          label: s.label,
          type: s.type,
          colorClass: s.colorClass,
        });
      });
    });
  });

  return { sessionsBySubject, sessionsByProf };
}

// Utils for teachers
const initialsFromUser = (u) => {
  if (!u) return '';
  const first = (u.first_name || '').trim();
  const last = (u.last_name || '').trim();
  if (first || last) return `${first[0] || ''}${last[0] || ''}`.toUpperCase();
  const name = (u.username || '').trim();
  return name ? name.slice(0, 2).toUpperCase() : '';
};
const colorFromId = (id) => {
  // Deterministic pastel color from string id
  const str = String(id || '');
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const h = Math.abs(hash) % 360;
  return `hsl(${h}, 70%, 45%)`;
};

const PlanFormationCreate = () => {
  const { selectedYear } = useSchoolYear();
  const [tab, setTab] = useState('generer'); // 'calendrier' | 'matieres' | 'profs' | 'templates' | 'generer' | 'plan' | 'taches'
  const [data, setData] = useState(DEFAULT_DATA);
  const [generatedY1, setGeneratedY1] = useState({ sessionsBySubject: {}, sessionsByProf: {} });
  const [generatedY2, setGeneratedY2] = useState({ sessionsBySubject: {}, sessionsByProf: {} });
  const [selectedProf, setSelectedProf] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [teacherColors, setTeacherColors] = useState({});
  const [showCal1, setShowCal1] = useState(false);
  const [showCal2, setShowCal2] = useState(false);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [planModalData, setPlanModalData] = useState(null);

  // Weeks & bands computed
  const weeks1 = useMemo(
    () => generateWeeks(data.calendrier.dateDebut, data.calendrier.dateFin),
    [data.calendrier.dateDebut, data.calendrier.dateFin]
  );
  const bands1 = useMemo(() => buildBlockedBands(weeks1, data.calendrier), [weeks1, data.calendrier]);
  const weeks2 = useMemo(
    () => generateWeeks(data.calendrier2.dateDebut, data.calendrier2.dateFin),
    [data.calendrier2.dateDebut, data.calendrier2.dateFin]
  );
  const bands2 = useMemo(() => buildBlockedBands(weeks2, data.calendrier2), [weeks2, data.calendrier2]);

  // Charger les données depuis l'API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // Charger les professeurs
        const resUsers = await axios.get('/api/users');
        const list = Array.isArray(resUsers.data?.data) ? resUsers.data.data : [];
        const onlyTeachers = list.filter((u) => u.role === 'teacher');
        setTeachers(onlyTeachers);
        setTeacherColors(buildTeacherColorMap(onlyTeachers));
        if (!selectedProf && onlyTeachers.length > 0) setSelectedProf(onlyTeachers[0].id);
        setLoadingTeachers(false);

        // Charger le plan de formation depuis l'API
        try {
          const res = await axios.get(`/api/plan-formation?school_year=${selectedYear}`);
          if (res.data && res.data.calendrier) {
            setData(res.data);
          }
        } catch (err) {
          console.log('Aucune donnée sauvegardée, utilisation des données par défaut');
        }
      } catch (e) {
        console.error('Erreur chargement données:', e);
        toast.error('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [selectedYear]); // recharger quand l'année scolaire change

  useEffect(() => {
    if (!selectedProf && teachers.length > 0) setSelectedProf(teachers[0].id);
  }, [teachers, selectedProf]);

  const belongsToClasse = (m, classe) => {
    const c = String(m.classe || '').toUpperCase();
    if (c === classe.toUpperCase()) return true;
    if (c.includes('+')) return c.split('+').map((s) => s.trim().toUpperCase()).includes(classe.toUpperCase());
    return false;
  };
  const filterDataByClasse = (d, classe) => ({
    ...d,
    matieres: (d.matieres || []).filter((m) => belongsToClasse(m, classe)),
  });
  const handleGenerate = () => {
    const res1 = autoGeneratePlan(weeks1, bands1, filterDataByClasse(data, 'BTS1'), teachers);
    const res2 = autoGeneratePlan(weeks2, bands2, filterDataByClasse(data, 'BTS2'), teachers);
    setGeneratedY1(res1);
    setGeneratedY2(res2);
    setTab('plan');
  };

  const exportPrint = () => {
    window.print();
  };

  // Sauvegarder les données
  const saveData = useCallback(async () => {
    try {
      setSaving(true);
      await axios.post('/api/plan-formation', {
        school_year: selectedYear,
        calendrier: data.calendrier,
        calendrier2: data.calendrier2,
        matieres: data.matieres,
        profs: data.profs,
        templates: data.templates
      });
      toast.success('Plan de formation sauvegardé');
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  }, [data, selectedYear]);

  // Sauvegarde automatique quand les données changent (avec debounce)
  const saveTimeoutRef = useRef(null);
  useEffect(() => {
    if (!loading && saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    if (!loading) {
      saveTimeoutRef.current = setTimeout(() => {
        saveData();
      }, 3000); // Sauvegarder 3 secondes après la dernière modification
    }
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [data, selectedYear, loading, saveData]);

  // Génération automatique quand l'onglet "plan" est affiché ou que les données changent
  useEffect(() => {
    if (tab !== 'plan') return;
    const res1 = autoGeneratePlan(weeks1, bands1, filterDataByClasse(data, 'BTS1'), teachers);
    const res2 = autoGeneratePlan(weeks2, bands2, filterDataByClasse(data, 'BTS2'), teachers);
    setGeneratedY1(res1);
    setGeneratedY2(res2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, data, weeks1, bands1, weeks2, bands2, teachers]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Générateur visuel — Plan de formation</h1>
          <p className="mt-1 text-sm text-gray-500">
            Configurez l&apos;année, les matières et professeurs, puis générez un plan annuel coloré.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn" onClick={() => setTab('generer')}>Données</button>
          <button className="btn btn-primary" onClick={handleGenerate}>Générer</button>
          <button className="btn" onClick={exportPrint}>Exporter / Imprimer</button>
          <button 
            className="btn flex items-center gap-2" 
            onClick={saveData}
            disabled={saving}
          >
            <Save className="w-4 h-4" />
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <TabButton active={tab === 'generer'} onClick={() => setTab('generer')}>Données</TabButton>
        <TabButton active={tab === 'plan'} onClick={() => setTab('plan')}>Plan annuel</TabButton>
        <TabButton active={tab === 'taches'} onClick={() => setTab('taches')}>Mes tâches</TabButton>
      </div>

      {tab === 'generer' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Boutons pour ouvrir les calendriers en modal */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-2">Calendriers</h2>
            <div className="text-xs text-gray-500 mb-3">Configurez séparément chaque année sans surcharger la page.</div>
            <div className="flex flex-wrap gap-2">
              <button type="button" className="btn btn-primary" onClick={() => setShowCal1(true)}>
                Ouvrir le calendrier — Année 1 (BTS1) • {data.calendrier.annee}
              </button>
              <button type="button" className="btn" onClick={() => setShowCal2(true)}>
                Ouvrir le calendrier — Année 2 (BTS2) • {data.calendrier2.annee}
              </button>
            </div>
          </div>

          {/* Matières (éditeur) */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Matières / Modules</h2>
            <SubjectEditor
              items={data.matieres}
              weeks1={weeks1}
              weeks2={weeks2}
              teachers={teachers}
              affectations={data.affectations || {}}
              onChangeAffectations={(aff) => setData((d) => ({ ...d, affectations: aff }))}
              onChange={(items) => setData((d) => ({ ...d, matieres: items }))}
            />
          </div>

          {/* Profs - sélection depuis Utilisateurs */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Professeurs</h2>
            {loadingTeachers ? (
              <div className="text-sm text-gray-500">Chargement des professeurs…</div>
            ) : (
              <div className="space-y-4">
                <div className="text-xs text-gray-500">
                  Sélectionnez le professeur responsable pour chaque matière. Les noms proviennent de la rubrique
                  &quot;Utilisateurs&quot; (rôle: professeur).
                </div>
                {(data.matieres || []).map((m) => {
                  const sel = data.affectations?.[m.id] || '';
                          return (
                    <div key={m.id} className="p-3 border rounded-md flex items-center justify-between gap-3">
                      <div>
                        <div className="font-medium text-gray-900">{m.nom}</div>
                        <div className="text-xs text-gray-500">{m.code} • {m.classe.toUpperCase()}</div>
                      </div>
                                <div className="flex items-center gap-2">
                        <select
                          className="input w-64"
                          value={sel}
                          onChange={(e) =>
                            setData((d) => ({
                              ...d,
                              affectations: { ...(d.affectations || {}), [m.id]: e.target.value },
                            }))
                          }
                        >
                          <option value="">— Choisir un professeur —</option>
                          {teachers.map((t) => {
                            const display =
                              (t.first_name && t.last_name) ? `${t.first_name} ${t.last_name}` : t.username;
                            return (
                              <option key={t.id} value={t.id}>
                                {display}
                              </option>
                            );
                          })}
                        </select>
                        {sel && (
                                  <span
                                    className="inline-flex items-center justify-center rounded-full text-[10px] font-bold px-1.5 py-0.5"
                            style={{ background: teacherColors[String(sel)] || colorFromId(sel), color: 'white' }}
                            title="Couleur prof"
                                  >
                            {initialsFromUser(teachers.find(t => String(t.id) === String(sel)))}
                                  </span>
                        )}
                                </div>
                                </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modales de calendrier */}
      <CalendarModal
        open={showCal1}
        title={`Calendrier — Année 1 (BTS1)`}
        calendrier={data.calendrier}
        onChangeCalendrier={(cal) => setData((d) => ({ ...d, calendrier: cal }))}
        onClose={() => setShowCal1(false)}
      />
      <CalendarModal
        open={showCal2}
        title={`Calendrier — Année 2 (BTS2)`}
        calendrier={data.calendrier2}
        onChangeCalendrier={(cal) => setData((d) => ({ ...d, calendrier2: cal }))}
        onClose={() => setShowCal2(false)}
      />

      {tab === 'plan' && (
        <>
          {/* Année 1 */}
          <div className="card overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">Plan — {data.calendrier.annee} (BTS1)</h2>
              <div className="text-xs text-gray-600 flex gap-2 items-center">
                <Badge colorClass={CATEGORY_COLORS.cours}>Cours</Badge>
                <Badge colorClass={CATEGORY_COLORS.tp}>TP</Badge>
                <Badge colorClass={CATEGORY_COLORS.td}>TD</Badge>
                <Badge colorClass={CATEGORY_COLORS.cours_tp}>Cours + TP</Badge>
                <Badge colorClass={CATEGORY_COLORS.cours_td}>Cours + TD</Badge>
                <Badge colorClass={CATEGORY_COLORS.projet}>Projet/SAÉ</Badge>
                <Badge colorClass={CATEGORY_COLORS.evaluation}>Évaluation</Badge>
                <Badge colorClass={CATEGORY_COLORS.ccf}>CCF</Badge>
                <Badge colorClass={CATEGORY_COLORS.vacance}>Vacances</Badge>
                <Badge colorClass={CATEGORY_COLORS.stage}>Stage</Badge>
                <Badge colorClass={CATEGORY_COLORS.examens}>Examens</Badge>
              </div>
            </div>
            <div className="min-w-[900px]">
              <table className="min-w-full border border-gray-200" style={{ borderCollapse: 'collapse' }}>
                <thead className="bg-gray-50">
                  {/* Ligne des mois fusionnés */}
                  <tr>
                    <th rowSpan={3} className="sticky left-0 bg-gray-50 z-20 border-b border-r px-2 py-2 text-left text-xs font-semibold text-gray-700 w-48">
                    </th>
                    {groupWeeksByMonth(weeks1).map((group, idx) => (
                      <th 
                        key={`month-${idx}`} 
                        colSpan={group.count}
                        className="border-b border-l border-r px-2 py-2 text-center text-[14px] font-bold text-gray-800 bg-gray-100"
                      >
                        {group.month}
                      </th>
                    ))}
                  </tr>
                  {/* Ligne des semaines */}
                  <tr>
                    {weeks1.map((w) => (
                      <th key={w.index} className="border-b border-l border-r px-2 py-2 text-center text-[11px] font-semibold text-gray-700">
                        {w.label}
                      </th>
                    ))}
                  </tr>
                  {/* Bandes bloquantes */}
                  <tr>
                    <th className="sticky left-0 bg-white z-10 border-b border-r px-2 py-1 text-left text-xs font-medium text-gray-700">
                    </th>
                    {weeks1.map((_, idx) => {
                      const types = bands1[idx]?.types || [];
                      if (types.length === 0) {
                        return <th key={idx} className="border-b border-l border-r px-1 py-1"></th>;
                      }
                      const label = types.map(t => t.label).join(' • ');
                      const mainType = types[0]?.type || 'vacance';
                      const color = CATEGORY_COLORS[mainType] || CATEGORY_COLORS.vacance;
                      return (
                        <th key={idx} className={`border-b border-l border-r px-1 py-1 ${color}`} title={label}>
                          <span className="text-[10px] font-medium">{types.map(t => t.label).join(' / ')}</span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {data.matieres.filter(m => belongsToClasse(m, 'BTS1')).map((m) => (
                    <tr key={m.id} className="bg-white border-b border-gray-300">
                      <td className="sticky left-0 bg-white z-10 border-r border-b border-gray-300 px-2 py-2 text-sm font-medium text-gray-900 w-48">
                            <div className="truncate">
                          {m.nom}
                          </div>
                        </td>
                      {(() => {
                        // Calculer les groupes fusionnés pour cette matière
                        const sessionsByWeek = weeks1.map((_, idx) => 
                          generatedY1.sessionsBySubject?.[m.id]?.[idx] || []
                        );
                        const mergedGroups = calculateMergedCells(sessionsByWeek);
                        
                        return mergedGroups.map((group, groupIdx) => {
                          const list = group.content;
                          const isEmpty = list.length === 0;
                          // Ne rendre que la première cellule du groupe avec colspan
                          return (
                            <td 
                              key={`group-${groupIdx}`} 
                              colSpan={group.count}
                              className="border border-gray-300 align-top px-1 py-1 min-w-[110px] cursor-pointer hover:bg-gray-50"
                              onClick={() => {
                                const weekIndex = group.startIndex;
                                setPlanModalData({
                                  classe: 'BTS1',
                                  weekStartIndex: weekIndex,
                                  weekEndIndex: weekIndex + group.count - 1,
                                  subjectId: m.id
                                });
                                setPlanModalOpen(true);
                              }}
                              title="Cliquer pour ajouter/modifier un contenu"
                            >
                              <div className="flex flex-col gap-1">
                                {(() => {
                                  // Organiser les sessions par professeur pour un meilleur alignement
                                  const sessionsByProf = {};
                                  list.forEach((s) => {
                                    const profId = s.prof?.id || 'no-prof';
                                    if (!sessionsByProf[profId]) {
                                      sessionsByProf[profId] = [];
                                    }
                                    sessionsByProf[profId].push(s);
                                  });
                                  
                                  // Trier les professeurs pour un affichage cohérent
                                  const profIds = Object.keys(sessionsByProf).sort();
                                  
                                  // Aplatir toutes les sessions triées par professeur
                                  return profIds.flatMap((profId) => {
                                    return sessionsByProf[profId].map((s, i) => {
                                      // Trouver la matière correspondante à cette session
                                      const matchingSubject = data.matieres.find(subj => {
                                        // Comparer les propriétés de la session avec la matière
                                        const sessionLabel = s.label || '';
                                        const subjectLabel = subj.nom || subj.code || '';
                                        
                                        // Vérifier si le label correspond (en tenant compte des préfixes)
                                        const labelMatch = sessionLabel.includes(subjectLabel) || 
                                                          subjectLabel.includes(sessionLabel) ||
                                                          (s.label && (s.label.includes(subj.nom) || s.label.includes(subj.code)));
                                        
                                        // Vérifier aussi le type
                                        const typeMatch = (s.type || 'cours') === (subj.type || 'cours');
                                        
                                        // Vérifier les compétences (si disponibles)
                                        const competencesMatch = Array.isArray(s.competences) && Array.isArray(subj.competences) &&
                                          s.competences.length > 0 && 
                                          s.competences.every(c => subj.competences.includes(c));
                                        
                                        return (labelMatch && typeMatch) || (labelMatch && competencesMatch);
                                      }) || data.matieres.find(subj => subj.id === m.id); // Fallback sur la matière de la ligne
                            
                                      return (
                                        <div 
                                          key={`${profId}-${i}`} 
                                          className={`rounded px-2 py-1 text-[11px] ${s.colorClass || CATEGORY_COLORS[s.type || 'cours'] || CATEGORY_COLORS.cours} cursor-pointer hover:opacity-80 transition-opacity`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const weekIndex = group.startIndex;
                                            
                                            // Si on a trouvé une matière correspondante, la pré-remplir
                                            if (matchingSubject) {
                                              setPlanModalData({
                                                classe: 'BTS1',
                                                weekStartIndex: weekIndex,
                                                weekEndIndex: weekIndex + group.count - 1,
                                                subject: matchingSubject,
                                                session: s,
                                                weekIndex: weekIndex,
                                                subjectId: matchingSubject.id
                                              });
                                            } else {
                                              // Sinon, créer une nouvelle matière avec les données de la session
                                              setPlanModalData({
                                                classe: 'BTS1',
                                                weekStartIndex: weekIndex,
                                                weekEndIndex: weekIndex + group.count - 1,
                                                session: s,
                                                weekIndex: weekIndex
                                              });
                                            }
                                            setPlanModalOpen(true);
                                          }}
                                          title="Cliquer pour modifier cette matière"
                                        >
                                          <div className="flex items-center justify-between gap-2">
                                            <span className="truncate">{s.label}</span>
                                            {s.prof && (
                                              <span
                                                className="ml-1 inline-flex items-center justify-center rounded-full text-[9px] font-bold px-1.5 py-0.5"
                                                style={{ background: s.prof.couleur, color: 'white' }}
                                                title={s.prof.nom}
                                              >
                                                {s.prof.initiales}
                                              </span>
                                            )}
                                          </div>
                                          {s.dureeSeanceH && (
                                            <div className="text-[10px] opacity-80 mt-0.5">
                                              {formatDuree(s.dureeSeanceH)}
                                            </div>
                                          )}
                                          {s.livrable && <div className="text-[10px] opacity-80">{s.livrable}</div>}
                                          {(Array.isArray(s.competences) && s.competences.length > 0) && (
                                            <div className="flex flex-wrap gap-1 mt-1">
                                              {s.competences.map((c) => (
                                                <span
                                                  key={c}
                                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-gray-100 text-gray-800 border"
                                                  title={COMPETENCE_LABELS[c] || 'Compétence'}
                                                >
                                                  {c}
                                                </span>
                                              ))}
                                            </div>
                                          )}
                                          {(Array.isArray(s.taches) && s.taches.length > 0) && (
                                            <div className="flex flex-wrap gap-1 mt-1">
                                              {s.taches.map((t) => (
                                                <span
                                                  key={t}
                                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-blue-50 text-blue-900 border border-blue-200"
                                                  title={TASK_LABELS[t] || 'Tâche'}
                                                >
                                                  {t}
                                                </span>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    });
                                  });
                                })()}
                                {list.length === 0 && (
                                  <div className="text-xs text-gray-400 text-center py-2">
                                    + Cliquer pour ajouter
                                  </div>
                                )}
                              </div>
                            </td>
                          );
                        });
                      })()}
                      </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Année 2 */}
          <div className="card overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">Plan — {data.calendrier2.annee} (BTS2)</h2>
            </div>
            <div className="min-w-[900px]">
              <table className="min-w-full border border-gray-200" style={{ borderCollapse: 'collapse' }}>
                <thead className="bg-gray-50">
                  {/* Ligne des mois fusionnés */}
                  <tr>
                    <th rowSpan={3} className="sticky left-0 bg-gray-50 z-20 border-b border-r px-2 py-2 text-left text-xs font-semibold text-gray-700 w-48">
                    </th>
                    {groupWeeksByMonth(weeks2).map((group, idx) => (
                      <th 
                        key={`month-${idx}`} 
                        colSpan={group.count}
                        className="border-b border-l border-r px-2 py-2 text-center text-[14px] font-bold text-gray-800 bg-gray-100"
                      >
                        {group.month}
                      </th>
                    ))}
                  </tr>
                  {/* Ligne des semaines */}
                  <tr>
                    {weeks2.map((w) => (
                      <th key={w.index} className="border-b border-l border-r px-2 py-2 text-center text-[11px] font-semibold text-gray-700">
                        {w.label}
                      </th>
                    ))}
                  </tr>
                  {/* Bandes bloquantes */}
                  <tr>
                    <th className="sticky left-0 bg-white z-10 border-b border-r px-2 py-1 text-left text-xs font-medium text-gray-700">
                    </th>
                    {weeks2.map((_, idx) => {
                      const types = bands2[idx]?.types || [];
                      if (types.length === 0) {
                        return <th key={idx} className="border-b border-l border-r px-1 py-1"></th>;
                      }
                      const label = types.map(t => t.label).join(' • ');
                      const mainType = types[0]?.type || 'vacance';
                      const color = CATEGORY_COLORS[mainType] || CATEGORY_COLORS.vacance;
                      return (
                        <th key={idx} className={`border-b border-l border-r px-1 py-1 ${color}`} title={label}>
                          <span className="text-[10px] font-medium">{types.map(t => t.label).join(' / ')}</span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {data.matieres.filter(m => belongsToClasse(m, 'BTS2')).map((m) => (
                    <tr key={m.id} className="bg-white border-b border-gray-300">
                      <td className="sticky left-0 bg-white z-10 border-r border-b border-gray-300 px-2 py-2 text-sm font-medium text-gray-900 w-48">
                            <div className="truncate">
                          {m.nom}
                          </div>
                        </td>
                      {(() => {
                        // Calculer les groupes fusionnés pour cette matière
                        const sessionsByWeek = weeks2.map((_, idx) => 
                          generatedY2.sessionsBySubject?.[m.id]?.[idx] || []
                        );
                        const mergedGroups = calculateMergedCells(sessionsByWeek);
                        
                        return mergedGroups.map((group, groupIdx) => {
                          const list = group.content;
                          const isEmpty = list.length === 0;
                          // Ne rendre que la première cellule du groupe avec colspan
                          return (
                            <td 
                              key={`group-${groupIdx}`} 
                              colSpan={group.count}
                              className="border-l border-r border-b border-gray-300 align-top px-1 py-1 min-w-[110px] cursor-pointer hover:bg-gray-50"
                              onClick={() => {
                                const weekIndex = group.startIndex;
                                setPlanModalData({
                                  classe: 'BTS2',
                                  weekStartIndex: weekIndex,
                                  weekEndIndex: weekIndex + group.count - 1,
                                  subjectId: m.id
                                });
                                setPlanModalOpen(true);
                              }}
                              title="Cliquer pour ajouter/modifier un contenu"
                            >
                              <div className="flex flex-col gap-1">
                                {(() => {
                                  // Organiser les sessions par professeur pour un meilleur alignement
                                  const sessionsByProf = {};
                                  list.forEach((s) => {
                                    const profId = s.prof?.id || 'no-prof';
                                    if (!sessionsByProf[profId]) {
                                      sessionsByProf[profId] = [];
                                    }
                                    sessionsByProf[profId].push(s);
                                  });
                                  
                                  // Trier les professeurs pour un affichage cohérent
                                  const profIds = Object.keys(sessionsByProf).sort();
                                  
                                  // Aplatir toutes les sessions triées par professeur
                                  return profIds.flatMap((profId) => {
                                    return sessionsByProf[profId].map((s, i) => {
                                      // Trouver la matière correspondante à cette session
                                      const matchingSubject = data.matieres.find(subj => {
                                        // Comparer les propriétés de la session avec la matière
                                        const sessionLabel = s.label || '';
                                        const subjectLabel = subj.nom || subj.code || '';
                                        
                                        // Vérifier si le label correspond (en tenant compte des préfixes)
                                        const labelMatch = sessionLabel.includes(subjectLabel) || 
                                                          subjectLabel.includes(sessionLabel) ||
                                                          (s.label && (s.label.includes(subj.nom) || s.label.includes(subj.code)));
                                        
                                        // Vérifier aussi le type
                                        const typeMatch = (s.type || 'cours') === (subj.type || 'cours');
                                        
                                        // Vérifier les compétences (si disponibles)
                                        const competencesMatch = Array.isArray(s.competences) && Array.isArray(subj.competences) &&
                                          s.competences.length > 0 && 
                                          s.competences.every(c => subj.competences.includes(c));
                                        
                                        return (labelMatch && typeMatch) || (labelMatch && competencesMatch);
                                      }) || data.matieres.find(subj => subj.id === m.id); // Fallback sur la matière de la ligne
                            
                                      return (
                                        <div 
                                          key={`${profId}-${i}`} 
                                          className={`rounded px-2 py-1 text-[11px] ${s.colorClass || CATEGORY_COLORS[s.type || 'cours'] || CATEGORY_COLORS.cours} cursor-pointer hover:opacity-80 transition-opacity`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const weekIndex = group.startIndex;
                                            
                                            // Si on a trouvé une matière correspondante, la pré-remplir
                                            if (matchingSubject) {
                                              setPlanModalData({
                                                classe: 'BTS2',
                                                weekStartIndex: weekIndex,
                                                weekEndIndex: weekIndex + group.count - 1,
                                                subject: matchingSubject,
                                                session: s,
                                                weekIndex: weekIndex,
                                                subjectId: matchingSubject.id
                                              });
                                            } else {
                                              // Sinon, créer une nouvelle matière avec les données de la session
                                              setPlanModalData({
                                                classe: 'BTS2',
                                                weekStartIndex: weekIndex,
                                                weekEndIndex: weekIndex + group.count - 1,
                                                session: s,
                                                weekIndex: weekIndex
                                              });
                                            }
                                            setPlanModalOpen(true);
                                          }}
                                          title="Cliquer pour modifier cette matière"
                                        >
                                          <div className="flex items-center justify-between gap-2">
                                            <span className="truncate">{s.label}</span>
                                            {s.prof && (
                                              <span
                                                className="ml-1 inline-flex items-center justify-center rounded-full text-[9px] font-bold px-1.5 py-0.5"
                                                style={{ background: s.prof.couleur, color: 'white' }}
                                                title={s.prof.nom}
                                              >
                                                {s.prof.initiales}
                                              </span>
                                            )}
                                          </div>
                                          {s.dureeSeanceH && (
                                            <div className="text-[10px] opacity-80 mt-0.5">
                                              {formatDuree(s.dureeSeanceH)}
                                            </div>
                                          )}
                                          {s.livrable && <div className="text-[10px] opacity-80">{s.livrable}</div>}
                                          {(Array.isArray(s.competences) && s.competences.length > 0) && (
                                            <div className="flex flex-wrap gap-1 mt-1">
                                              {s.competences.map((c) => (
                                                <span
                                                  key={c}
                                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-gray-100 text-gray-800 border"
                                                  title={COMPETENCE_LABELS[c] || 'Compétence'}
                                                >
                                                  {c}
                                                </span>
                                              ))}
                                            </div>
                                          )}
                                          {(Array.isArray(s.taches) && s.taches.length > 0) && (
                                            <div className="flex flex-wrap gap-1 mt-1">
                                              {s.taches.map((t) => (
                                                <span
                                                  key={t}
                                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-blue-50 text-blue-900 border border-blue-200"
                                                  title={TASK_LABELS[t] || 'Tâche'}
                                                >
                                                  {t}
                                                </span>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    });
                                  });
                                })()}
                              </div>
                              {list.length === 0 && (
                                <div className="text-xs text-gray-400 text-center py-2">
                                  + Cliquer pour ajouter
                                </div>
                              )}
                            </td>
                          );
                        });
                      })()}
                      </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Modal pour ajouter une matière depuis le plan */}
      <SubjectModal
        open={planModalOpen}
        initial={planModalData?.subject || null}
        onClose={() => {
          setPlanModalOpen(false);
          setPlanModalData(null);
        }}
        onSave={(subject, teacherId) => {
          if (subject.id && data.matieres.find(m => m.id === subject.id)) {
            // Matière existante - mettre à jour
            const updatedMatieres = data.matieres.map(m =>
              m.id === subject.id ? subject : m
            );
            let nextData = { ...data, matieres: updatedMatieres };

            // Mettre à jour les affectations si un professeur est défini
            if (teacherId) {
              const nextAff = { ...(data.affectations || {}) };
              nextAff[subject.id] = teacherId;
              nextData = { ...nextData, affectations: nextAff };
            }
            setData(nextData);
          } else {
            // Nouvelle matière - ajouter
            const newSubject = {
              ...subject,
              id: subject.id || `M${Date.now()}`
            };
            const updatedMatieres = [...(data.matieres || []), newSubject];
            let nextData = { ...data, matieres: updatedMatieres };

            // Mettre à jour les affectations si un professeur est défini
            if (teacherId) {
              const nextAff = { ...(data.affectations || {}) };
              nextAff[newSubject.id] = teacherId;
              nextData = { ...nextData, affectations: nextAff };
            }
            setData(nextData);
          }
          
          setPlanModalOpen(false);
          setPlanModalData(null);
        }}
        selectedTeacherId=""
        teachers={teachers}
        weeks1={weeks1}
        weeks2={weeks2}
        existingSubjects={data.matieres || []}
        fromPlan={true}
        defaultPeriod={planModalData}
      />

      {tab === 'taches' && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Mes tâches</h2>
            <select
              className="input w-56"
              value={selectedProf || ''}
              onChange={(e) => setSelectedProf(e.target.value)}
            >
              {teachers.map((t) => {
                const display =
                  (t.first_name && t.last_name) ? `${t.first_name} ${t.last_name}` : t.username;
                return (
                  <option key={t.id} value={t.id}>
                    {display}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="space-y-3">
            {selectedProf && (
              [...(generatedY1.sessionsByProf[String(selectedProf)] || []), ...(generatedY2.sessionsByProf[String(selectedProf)] || [])]
                .sort((a, b) => a.weekIndex - b.weekIndex)
                .map((t, i) => (
                  <div key={i} className="flex items-center justify-between p-2 border rounded">
                    <div className="flex items-center gap-3">
                      <Badge colorClass={t.colorClass}>{t.type.toUpperCase()}</Badge>
                      <div className="text-sm font-medium text-gray-900">{t.label}</div>
                    </div>
                    <div className="text-xs text-gray-600">{t.weekLabel}</div>
                  </div>
                ))
            )}
            {selectedProf && (!generatedY1.sessionsByProf[String(selectedProf)] && !generatedY2.sessionsByProf[String(selectedProf)]) && (
              <div className="text-sm text-gray-500">Aucune tâche générée pour ce professeur. Lancez la génération.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanFormationCreate;

